import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/lib/currency';
import { withBase } from '@/constants';
import type { Product } from '@/types/domain';

const CATEGORY_KEY: Record<string, string> = {
  standard: 'catalog.cat.standard',
  custom: 'catalog.cat.custom',
  luxury: 'catalog.cat.luxury',
};

const TAG_KEY: Record<string, string> = {
  new: 'catalog.tag.new',
  single: 'catalog.tag.single',
  double: 'catalog.tag.double',
  premium: 'catalog.tag.premium',
  project: 'catalog.tag.project',
};

const CAPACITY_KEY: Record<string, string> = {
  '1': 'catalog.singleCar',
  '2': 'catalog.doubleCar',
  multi: 'catalog.multiCar',
};

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { t, i18n } = useTranslation();
  const price = formatPrice(product.priceFrom, 'TRY', i18n.language);

  const primaryCta = !product.purchasable
    ? t('catalog.projectQuote')
    : price == null
      ? t('catalog.quote')
      : t('catalog.addToCart');

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative h-52 w-full overflow-hidden md:h-60">
        <img
          src={product.images[0]}
          alt={product.name}
          title={product.name}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-x-0 top-0 flex flex-wrap gap-1.5 p-3">
          {product.tags.map((tag) => (
            <span
              key={tag}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                tag === 'new'
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-50/90 text-foreground-800'
              }`}
            >
              {t(TAG_KEY[tag])}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
            {t(CATEGORY_KEY[product.category])}
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap text-xs text-foreground-500">
            <i className="ri-roadster-line" aria-hidden="true"></i>
            {t(CAPACITY_KEY[String(product.capacity)])}
          </span>
        </div>

        <h3 className="mt-3 font-heading text-xl text-foreground-950 md:text-2xl">
          {product.name}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600 md:text-base">
          {t(product.descKey)}
        </p>

        <div className="mt-5 flex items-end justify-between gap-3 border-t border-background-200 pt-4">
          <div className="flex flex-col">
            {price == null ? (
              <span className="text-sm text-foreground-500">{t('catalog.priceOnRequest')}</span>
            ) : (
              <>
                <span className="text-[11px] uppercase tracking-wide text-foreground-400">
                  {t('catalog.from')}
                </span>
                <span className="font-heading text-lg text-foreground-950">{price}</span>
              </>
            )}
          </div>
          <a
            href={withBase('/#iletisim')}
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-foreground-950 transition-colors hover:bg-primary-400"
          >
            {primaryCta}
          </a>
        </div>
      </div>
    </article>
  );
}